'use strict'

import { YouTubePlayer } from 'youtube-player/dist/types'

import { loadScript } from 'assets/core/js/common'

declare global {
  interface Window {
    YT: {
      Player(id: string, options: Record<string, unknown>): void
      loaded: number
    }
  }
}

let globalId = 0
let loadJs: null | Promise<void> = null

export default class YoutubePlayer {
  elt!: Element
  videoId!: string
  onStartPlaying!: (player: this) => void
  isReady!: boolean
  onReadyCb!: Array<() => void>
  ytPlayer!: YouTubePlayer

  constructor(elt: Element, videoId: string, onStartPlaying: (player: YoutubePlayer) => void) {
    this.elt = elt
    this.videoId = videoId
    this.onStartPlaying = onStartPlaying
    this.isReady = false
    this.onReadyCb = []
  }

  init(): Promise<void | null> {
    if (typeof window.YT === 'undefined') {
      if (loadJs === null) {
        // only load js promise once
        loadJs = loadScript('https://www.youtube.com/iframe_api', null, (): void => {
          // YT iframe api load external scripts that we have to wait
          // @ts-expect-error to check alter if still needed
          return new Promise<void>((resolve) => {
            ;(function waitForYT() {
              if (typeof window.YT !== 'undefined' && window.YT.loaded === 1) {
                resolve()
              } else {
                setTimeout(waitForYT, 100)
              }
            })()
          })
        })
      }
    } else {
      loadJs = Promise.resolve()
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadJs?.then(() => {
      const playerEltId = `video-${++globalId}`
      this.elt.innerHTML = `<div id="${playerEltId}"></div>`
      // @ts-expect-error object is defined
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/unbound-method
      this.ytPlayer = new window.YT.Player(playerEltId, {
        videoId: this.videoId,
        playerVars: {
          iv_load_policy: 3,
          modestbranding: 1,
          rel: 0, // as of september 2018, this doesn't deactivate related videos but limit them to the same channel
          showinfo: 0,
        },
        events: {
          onReady: (e: Event) => {
            // reassign the ytPlayer here to be sure to have a code safe if the event is called synchronously in the constructor
            // @ts-expect-error to check alter if still needed
            this.ytPlayer = e.target
            this.isReady = true
            this.onReadyCb.forEach((cb) => cb())
            this.onReadyCb = []
          },
          onStateChange: (e: Event) => {
            // @ts-expect-error property exists
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (e.data === window.YT.PlayerState.PLAYING) {
              this.onStartPlaying(this)
            }
          },
        },
      })
    })
    return loadJs
  }

  play(): YoutubePlayer {
    if (!this.isReady) {
      this.onReadyCb.push(() => this.play())
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.ytPlayer.playVideo()
    }
    return this
  }

  stop(): YoutubePlayer {
    if (!this.isReady) {
      this.onReadyCb.push(() => this.stop())
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.ytPlayer.stopVideo()
    }
    return this
  }
}

export function create(elt: Element, videoId: string, onStartPlaying: (player: YoutubePlayer) => void): YoutubePlayer {
  return new YoutubePlayer(elt, videoId, onStartPlaying)
}
